import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import AuthLayout from "../layouts/AuthLayout.jsx";
import apiClient from "../services/api.js";

export default function VerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    email: params.get("email") || "",
    code: ""
  });
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(location.state?.fromRegistration ? 30 : 0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "code" ? value.replace(/\D/g, "").slice(0, 6) : value
    }));
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    setError("");
    setStatus("");
    if (form.code.length !== 6) {
      setError("Enter the complete six-digit verification code.");
      return;
    }
    setVerifying(true);
    try {
      await apiClient.post("/auth/verify", {
        email: form.email.trim().toLowerCase(),
        code: form.code
      });
      navigate(`/login?email=${encodeURIComponent(form.email.trim())}&verified=1`, {
        replace: true,
        state: { verificationComplete: true }
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setStatus("");
    if (!form.email.trim() || resendCooldown > 0) return;
    setResending(true);
    try {
      const { data } = await apiClient.post("/auth/resend-verification", {
        email: form.email
      });
      setStatus(data?.message || "A new verification code has been sent.");
      setResendCooldown(30);
      if (data?.otp_debug) {
        setStatus(`Verification code resent. OTP: ${data.otp_debug}`);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Resend failed");
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout
      title="Verify your email"
      subtitle="Enter the six-digit code we sent to your inbox. It expires shortly for your security."
      steps={["Account", "Verify", "Sign in"]}
      currentStep={2}
      footer={
        <div className="flex flex-wrap items-center gap-2">
          <span>Already verified?</span>
          <Link className="font-semibold text-accent" to="/login">
            Sign in
          </Link>
        </div>
      }
    >
      <form className="space-y-5" onSubmit={handleVerify}>
        {location.state?.fromRegistration && (
          <div className="rounded-2xl border border-mint/30 bg-mint/10 px-4 py-3 text-sm text-emerald-800">
            Your account was created. Check your inbox to finish setup.
          </div>
        )}
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className="input mt-2"
            autoComplete="email"
            required
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
            OTP code
          </label>
          <input
            name="code"
            value={form.code}
            onChange={handleChange}
            className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-4 text-center text-2xl font-semibold tracking-[0.45em] text-ink outline-none transition focus:border-accent focus:ring-4 focus:ring-accent/10"
            placeholder="000000"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            autoFocus
            required
          />
          <p className="mt-2 text-center text-xs text-slate-500">
            {form.code.length}/6 digits entered
          </p>
        </div>
        {error && (
          <p role="alert" className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </p>
        )}
        {status && (
          <p aria-live="polite" className="rounded-2xl bg-mint/10 px-4 py-3 text-sm text-emerald-800">
            {status}
          </p>
        )}
        <button
          type="submit"
          disabled={verifying || resending || form.code.length !== 6}
          className="w-full rounded-2xl bg-accent px-4 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-accent/90"
        >
          {verifying ? "Verifying securely..." : "Verify and continue"}
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={verifying || resending || resendCooldown > 0 || !form.email.trim()}
          className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-slate-600"
        >
          {resending
            ? "Sending a new code..."
            : resendCooldown > 0
              ? `Resend available in ${resendCooldown}s`
              : "Resend verification code"}
        </button>
        <p className="text-center text-xs leading-5 text-slate-500">
          Didn’t receive it? Check your spam folder and confirm the email address above.
        </p>
      </form>
    </AuthLayout>
  );
}
